import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Receipt, 
  Calendar, 
  Users, 
  Truck, 
  DollarSign,
  ChevronDown,
  ChevronUp,
  Plus,
  History,
  Check,
  AlertCircle,
  Edit2,
  Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatNumber = (num) => {
  return new Intl.NumberFormat('en-NG').format(num);
};

const DistributorCommissionLedger = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'admin';
  const isDistributor = user?.role === 'distributor';
  
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [ledgerData, setLedgerData] = useState([]);
  const [commissionRates, setCommissionRates] = useState({ extra_large: 40, large: 30, medium: 20, small: 10, extra_large_crack: 7, large_crack: 5, medium_crack: 4, small_crack: 2, empty_feed_bag_percent: 5, frozen_chicken_percent: 5 });
  const [loading, setLoading] = useState(true);
  const [expandedDistributors, setExpandedDistributors] = useState({});
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [ratesDialogOpen, setRatesDialogOpen] = useState(false);
  const [editRates, setEditRates] = useState({ extra_large: 40, large: 30, medium: 20, small: 10, extra_large_crack: 7, large_crack: 5, medium_crack: 4, small_crack: 2, empty_feed_bag_percent: 5, frozen_chicken_percent: 5 });
  const [savingRates, setSavingRates] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [rateHistory, setRateHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [designatedDistributorId, setDesignatedDistributorId] = useState('');
  const [savingDesignated, setSavingDesignated] = useState(false);

  const handleSaveDesignated = async (newId) => {
    setSavingDesignated(true);
    try {
      await axios.put(
        `${API}/distributor-commission/designated-distributor?distributor_id=${newId}`,
        {},
        getAuthHeader()
      );
      setDesignatedDistributorId(newId);
      toast.success('Designated distributor updated');
      fetchLedgerData();
    } catch (error) {
      console.error('Error updating designated distributor:', error);
      toast.error('Failed to update designated distributor');
    } finally {
      setSavingDesignated(false);
    }
  };

  const fetchRateHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/commission-rates/history`, getAuthHeader());
      setRateHistory(res.data || []);
    } catch (e) {
      toast.error('Failed to load rate history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const openHistoryDialog = () => {
    setHistoryDialogOpen(true);
    fetchRateHistory();
  };
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    notes: ''
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const years = [];
  for (let y = currentDate.getFullYear() - 2; y <= currentDate.getFullYear() + 1; y++) {
    years.push(y);
  }

  useEffect(() => {
    fetchLedgerData();
  }, [selectedYear, selectedMonth]);

  const fetchLedgerData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/distributor-commission/ledger?year=${selectedYear}&month=${selectedMonth}`,
        getAuthHeader()
      );
      // Response now includes { ledger: [...], rates: {...} }
      setLedgerData(response.data.ledger || response.data);
      if (response.data.rates) {
        setCommissionRates(response.data.rates);
        setEditRates(response.data.rates);
      }
      if (response.data.designated_distributor_id !== undefined) {
        setDesignatedDistributorId(response.data.designated_distributor_id || '');
      }
    } catch (error) {
      console.error('Error fetching ledger:', error);
      toast.error('Failed to load commission ledger');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRates = async () => {
    setSavingRates(true);
    try {
      await axios.put(
        `${API}/commission-rates?extra_large=${editRates.extra_large}&large=${editRates.large}&medium=${editRates.medium}&small=${editRates.small}&extra_large_crack=${editRates.extra_large_crack ?? 7}&large_crack=${editRates.large_crack ?? 5}&medium_crack=${editRates.medium_crack ?? 4}&small_crack=${editRates.small_crack ?? 2}&empty_feed_bag_percent=${editRates.empty_feed_bag_percent ?? 5}&frozen_chicken_percent=${editRates.frozen_chicken_percent ?? 5}`,
        {},
        getAuthHeader()
      );
      toast.success('Commission rates updated successfully');
      setCommissionRates(editRates);
      setRatesDialogOpen(false);
      // Refresh ledger data to reflect new rates
      fetchLedgerData();
    } catch (error) {
      console.error('Error updating rates:', error);
      toast.error('Failed to update commission rates');
    } finally {
      setSavingRates(false);
    }
  };

  const toggleExpanded = (distributorId) => {
    setExpandedDistributors(prev => ({
      ...prev,
      [distributorId]: !prev[distributorId]
    }));
  };

  const openPaymentDialog = (distributor) => {
    setSelectedDistributor(distributor);
    setPaymentForm({
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      notes: ''
    });
    setPaymentDialogOpen(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDistributor || !paymentForm.amount) return;

    setSubmittingPayment(true);
    try {
      await axios.post(
        `${API}/distributor-commission/payment?distributor_id=${selectedDistributor.distributor_id}&year=${selectedYear}&month=${selectedMonth}&amount=${parseFloat(paymentForm.amount)}&payment_date=${paymentForm.payment_date}&payment_method=${paymentForm.payment_method}&notes=${encodeURIComponent(paymentForm.notes || '')}`,
        {},
        getAuthHeader()
      );
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      fetchLedgerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmittingPayment(false);
    }
  };

  // Calculate totals
  const totals = ledgerData.reduce((acc, d) => ({
    extra_large_crates: acc.extra_large_crates + (d.extra_large_crates_total || 0),
    large_crates: acc.large_crates + (d.large_crates_total || 0),
    medium_crates: acc.medium_crates + (d.medium_crates_total || 0),
    small_crates: acc.small_crates + (d.small_crates_total || 0),
    commission_due: acc.commission_due + d.total_commission_due,
    balance_bf: acc.balance_bf + d.balance_brought_forward,
    total_due: acc.total_due + d.total_amount_due,
    paid: acc.paid + d.amount_paid,
    balance: acc.balance + d.balance_due
  }), { extra_large_crates: 0, large_crates: 0, medium_crates: 0, small_crates: 0, commission_due: 0, balance_bf: 0, total_due: 0, paid: 0, balance: 0 });

  return (
    <div className="space-y-6" data-testid="distributor-commission-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Distributor Commission Ledger
          </h1>
          <p className="text-slate-600 mt-1">Track commissions due to distributors for affiliated customer pickups</p>
        </div>
        
        {/* Month/Year Selector */}
        <div className="flex gap-3">
          <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-36" data-testid="month-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Commission Rates Info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-blue-900">Commission Rates per Crate</h3>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-2" onClick={openHistoryDialog} data-testid="rate-history-btn">
                <History className="w-4 h-4" />
                History
              </Button>
            <Dialog open={ratesDialogOpen} onOpenChange={setRatesDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" data-testid="edit-rates-btn">
                  <Edit2 className="w-4 h-4" />
                  Edit Rates
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Commission Rates</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>X-Large Eggs (per crate)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-slate-500">₦</span>
                      <Input
                        type="number"
                        min="0"
                        value={editRates.extra_large}
                        onChange={(e) => setEditRates({...editRates, extra_large: parseFloat(e.target.value) || 0})}
                        data-testid="rate-xlarge-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Large Eggs (per crate)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-slate-500">₦</span>
                      <Input
                        type="number"
                        min="0"
                        value={editRates.large}
                        onChange={(e) => setEditRates({...editRates, large: parseFloat(e.target.value) || 0})}
                        data-testid="rate-large-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Medium Eggs (per crate)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-slate-500">₦</span>
                      <Input
                        type="number"
                        min="0"
                        value={editRates.medium}
                        onChange={(e) => setEditRates({...editRates, medium: parseFloat(e.target.value) || 0})}
                        data-testid="rate-medium-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Small Eggs (per crate)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-slate-500">₦</span>
                      <Input
                        type="number"
                        min="0"
                        value={editRates.small}
                        onChange={(e) => setEditRates({...editRates, small: parseFloat(e.target.value) || 0})}
                        data-testid="rate-small-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Large Cracked Eggs (per crate)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-slate-500">₦</span>
                      <Input
                        type="number"
                        min="0"
                        value={editRates.large_crack ?? 5}
                        onChange={(e) => setEditRates({...editRates, large_crack: parseFloat(e.target.value) || 0})}
                        data-testid="rate-large-crack-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>X-Large Cracked Eggs (per crate)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-slate-500">₦</span>
                      <Input
                        type="number"
                        min="0"
                        value={editRates.extra_large_crack ?? 7}
                        onChange={(e) => setEditRates({...editRates, extra_large_crack: parseFloat(e.target.value) || 0})}
                        data-testid="rate-xlarge-crack-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Medium Cracked Eggs (per crate)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-slate-500">₦</span>
                      <Input
                        type="number"
                        min="0"
                        value={editRates.medium_crack ?? 4}
                        onChange={(e) => setEditRates({...editRates, medium_crack: parseFloat(e.target.value) || 0})}
                        data-testid="rate-medium-crack-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Small Cracked Eggs (per crate)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-slate-500">₦</span>
                      <Input
                        type="number"
                        min="0"
                        value={editRates.small_crack ?? 2}
                        onChange={(e) => setEditRates({...editRates, small_crack: parseFloat(e.target.value) || 0})}
                        data-testid="rate-small-crack-input"
                      />
                    </div>
                  </div>
                  <div className="border-t pt-3 mt-1">
                    <Label className="flex items-center gap-2 text-amber-800 font-semibold">
                      Empty Feed Bags (% of Naira value sold)
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        value={editRates.empty_feed_bag_percent ?? 5}
                        onChange={(e) => setEditRates({...editRates, empty_feed_bag_percent: parseFloat(e.target.value) || 0})}
                        data-testid="rate-empty-feed-bag-input"
                      />
                      <span className="text-slate-500">%</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Commission = this % × total ₦ value of empty feed bags sold.</p>
                  </div>
                  <div className="border-t pt-3 mt-1">
                    <Label className="flex items-center gap-2 text-orange-800 font-semibold">
                      Frozen Chicken (% of Naira value sold)
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        value={editRates.frozen_chicken_percent ?? 5}
                        onChange={(e) => setEditRates({...editRates, frozen_chicken_percent: parseFloat(e.target.value) || 0})}
                        data-testid="rate-frozen-chicken-input"
                      />
                      <span className="text-slate-500">%</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Commission = this % × total ₦ value of frozen chicken sold.</p>
                  </div>
                  <Button 
                    onClick={handleSaveRates} 
                    disabled={savingRates}
                    className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                    data-testid="save-rates-btn"
                  >
                    {savingRates ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Rates
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Rate Change History Dialog */}
            <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-blue-600" />
                    Commission Rate Change History
                  </DialogTitle>
                </DialogHeader>
                <div className="py-2" data-testid="rate-history-list">
                  {loadingHistory ? (
                    <div className="flex items-center justify-center py-10 text-slate-400">
                      <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mr-2" /> Loading…
                    </div>
                  ) : rateHistory.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-10">No rate changes recorded yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {rateHistory.map((h, i) => (
                        <div key={h.id || i} className="border border-slate-200 rounded-lg p-3" data-testid={`rate-history-entry-${i}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-slate-800">{h.changed_by || 'Unknown'}</span>
                            <span className="text-xs text-slate-500">
                              {h.changed_at ? new Date(h.changed_at).toLocaleString() : ''}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {(h.changes || []).map((c, j) => (
                              <div key={j} className="flex items-center gap-2 text-sm">
                                <span className="text-slate-600 w-40">{c.label || c.field}</span>
                                <span className="text-red-600 line-through">{formatCurrency(c.old || 0)}</span>
                                <span className="text-slate-400">→</span>
                                <span className="text-emerald-700 font-semibold">{formatCurrency(c.new || 0)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div className="bg-white/70 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-purple-700">{formatCurrency(commissionRates.extra_large)}</div>
            <div className="text-slate-600">X-Large (per crate)</div>
          </div>
          <div className="bg-white/70 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-blue-700">{formatCurrency(commissionRates.large)}</div>
            <div className="text-slate-600">Large (per crate)</div>
          </div>
          <div className="bg-white/70 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-green-700">{formatCurrency(commissionRates.medium)}</div>
            <div className="text-slate-600">Medium (per crate)</div>
          </div>
          <div className="bg-white/70 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-amber-700">{formatCurrency(commissionRates.small)}</div>
            <div className="text-slate-600">Small (per crate)</div>
          </div>
          <div className="bg-white/70 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-rose-700">{formatCurrency(commissionRates.extra_large_crack ?? 7)}</div>
            <div className="text-slate-600">X-Large Cracked (per crate)</div>
          </div>
          <div className="bg-white/70 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-red-700">{formatCurrency(commissionRates.large_crack ?? 5)}</div>
            <div className="text-slate-600">Large Cracked (per crate)</div>
          </div>
          <div className="bg-white/70 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-red-600">{formatCurrency(commissionRates.medium_crack ?? 4)}</div>
            <div className="text-slate-600">Medium Cracked (per crate)</div>
          </div>
          <div className="bg-white/70 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-red-500">{formatCurrency(commissionRates.small_crack ?? 2)}</div>
            <div className="text-slate-600">Small Cracked (per crate)</div>
          </div>
          <div className="bg-amber-100 rounded-lg p-3 text-center border border-amber-300">
            <div className="text-lg font-bold text-amber-800">{commissionRates.empty_feed_bag_percent ?? 5}%</div>
            <div className="text-slate-600">Empty Feed Bags (of value)</div>
          </div>
          <div className="bg-orange-100 rounded-lg p-3 text-center border border-orange-300">
            <div className="text-lg font-bold text-orange-800">{commissionRates.frozen_chicken_percent ?? 5}%</div>
            <div className="text-slate-600">Frozen Chicken (of value)</div>
          </div>
        </div>
      </div>

      {/* Designated Distributor for Empty Bags + Frozen Chicken commission */}
      {!isDistributor && (
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4" data-testid="designated-distributor-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-start gap-2">
            <Truck className="w-5 h-5 text-orange-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-orange-900">Empty Bags &amp; Frozen Chicken Commission Recipient</h3>
              <p className="text-sm text-orange-700 mt-0.5">
                This distributor earns the Empty Feed Bags &amp; Frozen Chicken commission on <span className="font-medium">all farm sales</span>.
              </p>
            </div>
          </div>
          {isAdmin ? (
            <Select
              value={designatedDistributorId || undefined}
              onValueChange={handleSaveDesignated}
              disabled={savingDesignated}
            >
              <SelectTrigger className="w-full md:w-72 bg-white" data-testid="designated-distributor-select">
                <SelectValue placeholder="Select distributor" />
              </SelectTrigger>
              <SelectContent>
                {ledgerData.map(d => (
                  <SelectItem key={d.distributor_id} value={d.distributor_id}>{d.distributor_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm font-semibold text-orange-900 bg-white rounded-lg px-3 py-2 border border-orange-200" data-testid="designated-distributor-readonly">
              {(ledgerData.find(d => d.distributor_id === designatedDistributorId) || {}).distributor_name || '—'}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-600 mb-2">
            <Receipt className="w-4 h-4" />
            <span className="text-sm">Total Commission Due</span>
          </div>
          <div className="text-2xl font-bold text-slate-900">{formatCurrency(totals.commission_due)}</div>
          <div className="text-xs text-slate-500 mt-1">This month</div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-600 mb-2">
            <History className="w-4 h-4" />
            <span className="text-sm">Balance B/F</span>
          </div>
          <div className="text-2xl font-bold text-orange-600">{formatCurrency(totals.balance_bf)}</div>
          <div className="text-xs text-slate-500 mt-1">From previous months</div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-600 mb-2">
            <Check className="w-4 h-4" />
            <span className="text-sm">Amount Paid</span>
          </div>
          <div className="text-2xl font-bold text-emerald-600">{formatCurrency(totals.paid)}</div>
          <div className="text-xs text-slate-500 mt-1">This month</div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-600 mb-2">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">Balance Due</span>
          </div>
          <div className={`text-2xl font-bold ${totals.balance > 0 ? 'text-red-600' : 'text-slate-900'}`}>
            {formatCurrency(totals.balance)}
          </div>
          <div className="text-xs text-slate-500 mt-1">Outstanding</div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          <span className="ml-3 text-slate-600">Loading commission data...</span>
        </div>
      ) : ledgerData.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <Truck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600">No Distributors Found</h3>
          <p className="text-slate-500">No distributors are registered in the system.</p>
        </div>
      ) : (
        /* Distributor Cards */
        <div className="space-y-4">
          {ledgerData.map((distributor) => (
            <div 
              key={distributor.distributor_id} 
              data-testid={`distributor-card-${distributor.distributor_id}`}
              className="bg-white rounded-xl border shadow-sm overflow-hidden"
            >
              {/* Distributor Header */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleExpanded(distributor.distributor_id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                    {distributor.distributor_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{distributor.distributor_name}</h3>
                    <p className="text-sm text-slate-500">
                      {distributor.affiliated_customers.length} affiliated customer{distributor.affiliated_customers.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-sm text-slate-500">Balance Due</div>
                    <div className={`text-lg font-bold ${distributor.balance_due > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {formatCurrency(distributor.balance_due)}
                    </div>
                  </div>
                  {expandedDistributors[distributor.distributor_id] ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {expandedDistributors[distributor.distributor_id] && (
                <div className="border-t bg-slate-50 p-4 space-y-4">
                  {/* Crate Pickup Summary */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Crates Picked Up by Affiliated Customers</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-slate-500 mb-1">X-Large Crates</div>
                        <div className="text-xl font-bold text-purple-700">{formatNumber(distributor.extra_large_crates_total || 0)}</div>
                        <div className="text-sm text-slate-600">{formatCurrency(distributor.extra_large_commission || 0)}</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-slate-500 mb-1">Large Crates</div>
                        <div className="text-xl font-bold text-blue-700">{formatNumber(distributor.large_crates_total || 0)}</div>
                        <div className="text-sm text-slate-600">{formatCurrency(distributor.large_commission)}</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-slate-500 mb-1">Medium Crates</div>
                        <div className="text-xl font-bold text-green-700">{formatNumber(distributor.medium_crates_total || 0)}</div>
                        <div className="text-sm text-slate-600">{formatCurrency(distributor.medium_commission)}</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-slate-500 mb-1">Small Crates</div>
                        <div className="text-xl font-bold text-amber-700">{formatNumber(distributor.small_crates_total || 0)}</div>
                        <div className="text-sm text-slate-600">{formatCurrency(distributor.small_commission)}</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-slate-500 mb-1">X-Large Cracked Crates</div>
                        <div className="text-xl font-bold text-rose-700">{formatNumber(distributor.extra_large_crack_crates_total || 0)}</div>
                        <div className="text-sm text-slate-600">{formatCurrency(distributor.extra_large_crack_commission || 0)}</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-slate-500 mb-1">Large Cracked Crates</div>
                        <div className="text-xl font-bold text-red-700">{formatNumber(distributor.large_crack_crates_total || 0)}</div>
                        <div className="text-sm text-slate-600">{formatCurrency(distributor.large_crack_commission || 0)}</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-slate-500 mb-1">Medium Cracked Crates</div>
                        <div className="text-xl font-bold text-red-600">{formatNumber(distributor.medium_crack_crates_total || 0)}</div>
                        <div className="text-sm text-slate-600">{formatCurrency(distributor.medium_crack_commission || 0)}</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-slate-500 mb-1">Small Cracked Crates</div>
                        <div className="text-xl font-bold text-red-500">{formatNumber(distributor.small_crack_crates_total || 0)}</div>
                        <div className="text-sm text-slate-600">{formatCurrency(distributor.small_crack_commission || 0)}</div>
                      </div>
                    </div>
                    {/* Empty Feed Bags + Frozen Chicken commission (designated distributor only) */}
                    {distributor.is_designated_distributor && (
                    <>
                    <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="empty-feed-bag-commission-block">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="text-xs text-amber-700 font-medium mb-0.5">Empty Feed Bags Sold (value) — whole farm</div>
                          <div className="text-lg font-bold text-amber-900">{formatCurrency(distributor.empty_feed_bag_value_total || 0)}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-amber-700 font-medium mb-0.5">Rate</div>
                          <div className="text-lg font-bold text-amber-900">{distributor.empty_feed_bag_percent ?? commissionRates.empty_feed_bag_percent ?? 5}%</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-amber-700 font-medium mb-0.5">Empty Bags Commission</div>
                          <div className="text-lg font-bold text-emerald-700" data-testid="empty-feed-bag-commission-value">{formatCurrency(distributor.empty_feed_bag_commission || 0)}</div>
                        </div>
                      </div>
                    </div>
                    {/* Frozen Chicken commission */}
                    <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg p-3" data-testid="frozen-chicken-commission-block">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="text-xs text-orange-700 font-medium mb-0.5">Frozen Chicken Sold (value) — whole farm</div>
                          <div className="text-lg font-bold text-orange-900">{formatCurrency(distributor.frozen_chicken_value_total || 0)}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-orange-700 font-medium mb-0.5">Rate</div>
                          <div className="text-lg font-bold text-orange-900">{distributor.frozen_chicken_percent ?? commissionRates.frozen_chicken_percent ?? 5}%</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-orange-700 font-medium mb-0.5">Frozen Chicken Commission</div>
                          <div className="text-lg font-bold text-emerald-700" data-testid="frozen-chicken-commission-value">{formatCurrency(distributor.frozen_chicken_commission || 0)}</div>
                        </div>
                      </div>
                    </div>
                    </>
                    )}
                  </div>

                  {/* Commission Summary */}
                  <div className="bg-white rounded-lg p-4 border">
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Commission Summary</h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                      <div>
                        <div className="text-slate-500">Commission (This Month)</div>
                        <div className="font-semibold">{formatCurrency(distributor.total_commission_due)}</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Balance B/F</div>
                        <div className="font-semibold text-orange-600">{formatCurrency(distributor.balance_brought_forward)}</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Total Due</div>
                        <div className="font-semibold">{formatCurrency(distributor.total_amount_due)}</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Paid</div>
                        <div className="font-semibold text-emerald-600">{formatCurrency(distributor.amount_paid)}</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Balance Due</div>
                        <div className={`font-semibold ${distributor.balance_due > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {formatCurrency(distributor.balance_due)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Affiliated Customers */}
                  {distributor.affiliated_customers.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 mb-2">Affiliated Customers</h4>
                      <div className="flex flex-wrap gap-2">
                        {distributor.affiliated_customers.map((customer) => (
                          <button
                            type="button"
                            key={customer.id}
                            onClick={() => navigate(`/customer-account-records?customer=${customer.id}`)}
                            title={`Open ${customer.name}'s Account Record`}
                            className="bg-white border px-3 py-1 rounded-full text-sm text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 hover:underline transition-colors cursor-pointer"
                            data-testid={`affiliated-customer-link-${customer.id}`}
                          >
                            {customer.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Record Payment Button - Only for Admin */}
                  {isAdmin && (
                    <div className="flex justify-end pt-2">
                      <Button 
                        onClick={(e) => {
                          e.stopPropagation();
                          openPaymentDialog(distributor);
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`record-payment-btn-${distributor.distributor_id}`}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Record Payment
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>
              Record Commission Payment
            </DialogTitle>
          </DialogHeader>
          {selectedDistributor && (
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="text-sm text-slate-500">Distributor</div>
                <div className="font-medium">{selectedDistributor.distributor_name}</div>
                <div className="text-sm text-slate-600 mt-1">
                  Balance Due: <span className="font-semibold text-red-600">{formatCurrency(selectedDistributor.balance_due)}</span>
                </div>
              </div>
              
              <div>
                <Label htmlFor="amount">Payment Amount (Naira)</Label>
                <Input
                  id="amount"
                  type="number"
                  data-testid="payment-amount-input"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="Enter amount"
                  required
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <Label htmlFor="payment_date">Payment Date</Label>
                <Input
                  id="payment_date"
                  type="date"
                  data-testid="payment-date-input"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="payment_method">Payment Method</Label>
                <Select 
                  value={paymentForm.payment_method} 
                  onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}
                >
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="payment-notes-input"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  placeholder="Add any notes about this payment"
                  rows={2}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  type="submit" 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={submittingPayment}
                  data-testid="submit-payment-btn"
                >
                  {submittingPayment ? 'Recording...' : 'Record Payment'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setPaymentDialogOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DistributorCommissionLedger;
